import { formatMoney, type ReportDay } from '@fdt/shared';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useEffect, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../i18n';

/**
 * Sales and collections, one pair of bars per day.
 *
 * Drawn as plain SVG rather than with a charting library. The panel needs one
 * chart shape; MUI X Charts or Recharts would add a dependency the size of the
 * rest of the app to draw forty rectangles, and would still have to be taught
 * this theme's colours and the locale's money format. The two things a library
 * genuinely gives — axis ticks that pick sensible intervals, and tooltips — are
 * a `niceCeiling()` and a `<title>` here.
 *
 * Sales and collections are separate bars rather than one stacked column,
 * because they are not parts of a whole: money collected today may belong to a
 * delivery from three weeks ago, and stacking them would invent a total that
 * means nothing.
 */

const HEIGHT = 240;
const PADDING = { top: 16, right: 8, bottom: 28, left: 64 };
const MIN_BAR = 2;

/** A round number at or above the peak, so the axis label reads properly. */
function niceCeiling(value: number): number {
  if (value <= 0) return 100;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 2.5, 5, 10]) {
    const candidate = step * magnitude;
    if (candidate >= value) return candidate;
  }
  return 10 * magnitude;
}

function useWidth(): [RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(720);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const observer = new ResizeObserver((entries) => {
      // Never zero: a chart laid out at no width would divide by it.
      const measured = entries[0]?.contentRect.width ?? 0;
      setWidth(Math.max(320, Math.round(measured)));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

export function DailyChart({ days }: { days: ReportDay[] }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const locale = currentLocale();
  const [ref, width] = useWidth();

  const peak = Math.max(
    0,
    ...days.map((day) => Math.max(Number.parseFloat(day.salesNet), Number.parseFloat(day.collected))),
  );
  const ceiling = niceCeiling(peak);

  const plotWidth = Math.max(1, width - PADDING.left - PADDING.right);
  const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;
  const slot = plotWidth / Math.max(1, days.length);
  // Two bars per day with a gap, and never thinner than a hairline.
  const barWidth = Math.max(1, Math.min(14, (slot - 2) / 2));

  const y = (value: number) => PADDING.top + plotHeight - (value / ceiling) * plotHeight;
  const barHeight = (value: number) =>
    value <= 0 ? 0 : Math.max(MIN_BAR, (value / ceiling) * plotHeight);

  const salesColour = theme.palette.secondary.main;
  const collectedColour = theme.palette.success.main;

  // Fifths rather than quarters: every ceiling `niceCeiling` can produce
  // divides into five whole numbers, so no axis label reads 62,5 — a tick
  // nobody can place is worse than one tick fewer.
  const gridValues = [0, 0.2, 0.4, 0.6, 0.8, 1].map((share) => share * ceiling);

  // A label under every day is unreadable past a fortnight; thin them out so
  // roughly ten remain, always including the last day.
  const labelEvery = Math.max(1, Math.ceil(days.length / 10));

  return (
    <Box ref={ref} sx={{ width: '100%' }}>
      <Stack direction="row" spacing={2} sx={{ mb: 1, flexWrap: 'wrap' }}>
        <Legend colour={salesColour} label={t('reports.dashboard.salesNet')} />
        <Legend colour={collectedColour} label={t('reports.dashboard.collected')} />
      </Stack>

      <svg
        width={width}
        height={HEIGHT}
        role="img"
        aria-label={t('reports.dashboard.perDay')}
        style={{ display: 'block', overflow: 'visible' }}
      >
        {gridValues.map((value) => (
          <g key={value}>
            <line
              x1={PADDING.left}
              x2={width - PADDING.right}
              y1={y(value)}
              y2={y(value)}
              stroke={theme.palette.divider}
              strokeWidth={1}
            />
            <text
              x={PADDING.left - 8}
              y={y(value) + 4}
              textAnchor="end"
              fontSize={11}
              fill={theme.palette.text.secondary}
              fontVariant="tabular-nums"
            >
              {compactMoney(value, locale)}
            </text>
          </g>
        ))}

        {days.map((day, index) => {
          const sales = Number.parseFloat(day.salesNet);
          const collected = Number.parseFloat(day.collected);
          const left = PADDING.left + index * slot + (slot - barWidth * 2 - 2) / 2;

          return (
            <g key={day.day}>
              <title>
                {`${day.day}\n${t('reports.dashboard.salesNet')}: ${formatMoney(day.salesNet, locale)}\n${t('reports.dashboard.returnsNet')}: ${formatMoney(day.returnsNet, locale)}\n${t('reports.dashboard.collected')}: ${formatMoney(day.collected, locale)}`}
              </title>
              <rect
                x={left}
                y={PADDING.top + plotHeight - barHeight(sales)}
                width={barWidth}
                height={barHeight(sales)}
                rx={2}
                fill={salesColour}
                opacity={sales > 0 ? 0.9 : 0.25}
              />
              <rect
                x={left + barWidth + 2}
                y={PADDING.top + plotHeight - barHeight(collected)}
                width={barWidth}
                height={barHeight(collected)}
                rx={2}
                fill={collectedColour}
                opacity={collected > 0 ? 0.9 : 0.25}
              />
              {(index % labelEvery === 0 || index === days.length - 1) && (
                <text
                  x={PADDING.left + index * slot + slot / 2}
                  y={HEIGHT - 8}
                  textAnchor="middle"
                  fontSize={11}
                  fill={theme.palette.text.secondary}
                >
                  {day.day.slice(8)}/{day.day.slice(5, 7)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </Box>
  );
}

function Legend({ colour, label }: { colour: string; label: string }) {
  return (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
      <Box sx={{ width: 10, height: 10, borderRadius: 0.5, backgroundColor: colour }} />
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
    </Stack>
  );
}

/** Axis labels: thousands abbreviated, because a full euro figure does not fit. */
function compactMoney(value: number, locale: string): string {
  if (value === 0) return '0';
  if (value >= 1000) {
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value / 1000)}k`;
  }
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
}
